/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import dayjs from 'dayjs';
import Style from './../css/LoanHistory.module.css';
import { Table, TableHead, TableRow, TableBody, TableCell, Alert } from '@mui/material';
import EnvelopeWithBox from './../components/EnvelopeWithBox';
import ExpandableRow from '../components/ExpandableRow';
import useGetServicingPayment from './../common/hooks/useGetServicingPayment';
import LoadingModal from '../components/modals/LoadingModal';
import NumericCell from '../components/NumericCell';
import { useLoanContext } from '../contexts/LoanContext';
import { ServicingPayment } from '../components/forms/LoanHistoryTransactionForm';
import { LoanHistoryProvider, useLoanHistoryContext } from '../contexts/LoanHistoryTransactionContext';
import useGetEventDate from '../common/hooks/useGetEventDate';
import useScreenWidth from '../common/hooks/useScreenWidth';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContext';
import { useSnackbar, SnackbarProvider } from 'notistack';
import { isStrEmptyOrNull, formatDollars, formatAbbrevDateMMddyyyy } from '../common/UtilityFunctions';

type Payment = {
  date: string;
  description: string;
  amount: string;
  paidTo: string;
  principal: string;
  balance: string;
  interest: string;
  escrow: string;
  lateCharge: string;
  other: string;
  // otherDesc: string;
  prinCurlAlloc: string;
};

function BorrowerPayHistory() {
  const dateFormatConst = 'MM/DD/YYYY';
  const { loanNumber } = useLoanContext();
  const { servicingLoanPayments, servicingErrorMessages, loadingServicing } = useGetServicingPayment(loanNumber);
  const { isPaymentApplicableBasedOnMigration, getRemovedDuplicatesServPayments } = useLoanHistoryContext();
  const { getEventDateInfo } = useGetEventDate();
  const { token } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  const getFilteredServicingPayments = useCallback(async (): Promise<Payment[]> => {
    const payments = [];

    const migrationEvent = await getEventDateInfo(loanNumber, 'S7H');

    const removedDuplicatesServPayments = getRemovedDuplicatesServPayments(servicingLoanPayments);

    const filteredPaymentList = removedDuplicatesServPayments
      .filter((pmt) => pmt.mcdTransType !== '99' && pmt.posted === true)
      .filter((pmt) => isPaymentApplicableBasedOnMigration(pmt, migrationEvent));
    //sort the payments based on postDate this will go from oldest to newest
    filteredPaymentList.sort((item1, item2) => {
      return item1.postDate - item2.postDate;
    });
    var paymentDate = 0;
    const paymentDateArray = [];
    paymentDateArray.push(0);
    filteredPaymentList.forEach((item: ServicingPayment) => {
      if (
        // item.mcdTransSuffix !== 'V' &&
        // !(item.mcdTransType === '27' && item.mcdTransSuffix === 'T') &&
        item.mcdTransType !== '99' &&
        item.posted === true
      ) {
        const other =
          item.assessmentAlloc +
          item.assessmntAcrdAlloc +
          item.assessmntWvdAlloc +
          item.corpAdvAcrdAlloc +
          item.corpAdvAlloc +
          item.corpAdvWvdAlloc +
          item.deferInterestAlloc +
          item.deferPaymentAlloc +
          item.deferPrincipalAlloc +
          item.escrow2Alloc +
          item.escrow3Alloc +
          item.lateChrgAcrdAlloc +
          item.lateChrgWvdAlloc +
          item.lossDraftAlloc +
          item.negAmAlloc +
          item.otherSuspenseAlloc +
          item.repairAlloc +
          item.subSuspenseAlloc +
          item.suspenseAlloc +
          item.suspenseUsedAlloc +
          item.agencyPrinSubsidyAlloc +
          item.agencyIntSubsidyAlloc;
        const totPrin = item.principalAlloc + item.prinAdjusted;

        const miscList = {
          'assessment fee paid': item.assessmentAlloc,
          'assessed assessment fee': item.assessmntAcrdAlloc,
          'assessment fee waived': item.assessmntWvdAlloc,
          'corp adv assessed': item.corpAdvAcrdAlloc,
          'corp adv paid': item.corpAdvAlloc,
          'corp adv waived': item.corpAdvWvdAlloc,
          'defer interest': item.deferInterestAlloc,
          'defer payment': item.deferPaymentAlloc,
          'defer principal': item.deferPrincipalAlloc,
          'escrow 2': item.escrow2Alloc,
          'escrow 3': item.escrow3Alloc,
          'late charge assessed': item.lateChrgAcrdAlloc,
          'late charge waived': item.lateChrgWvdAlloc,
          'loss draft': item.lossDraftAlloc,
          'negative amortization': item.negAmAlloc,
          'other suspense': item.otherSuspenseAlloc,
          repairs: item.repairAlloc,
          subsuspense: item.subSuspenseAlloc,
          suspense: item.suspenseAlloc,
          'suspense used': item.suspenseUsedAlloc,
          'principal subsidy': item.agencyPrinSubsidyAlloc,
          'interest subsidy': item.agencyIntSubsidyAlloc,
        };
        // const otherElements = Object.keys(miscList).filter((key) => miscList[key] !== 0);
        // const otherStr = otherElements.join(', ');

        if (item.paymentDate && item.mcdTransType !== '03' && item.mcdTransType !== '04')
          paymentDateArray.push(item.paymentDate);
        else if (item.paymentDate) paymentDateArray.pop();
        paymentDate = paymentDateArray[paymentDateArray.length - 1];

        payments.push({
          date: item.postDate ? dayjs(item.postDate * 1000).format(dateFormatConst) : '',
          description: item.transactionDescription,
          amount: formatDollars(item.amount.toFixed(2)),
          paidTo: paymentDate ? dayjs(paymentDate * 1000).format(dateFormatConst) : '',
          principal: formatDollars(totPrin.toString()),
          prinCurlAlloc: formatDollars(item.prinCurtlAlloc.toFixed(2)),
          balance: formatDollars(item.afterPostUpbBal.toFixed(2)),
          interest: formatDollars(item.interestAlloc.toFixed(2)),
          escrow: formatDollars(item.escrowAlloc.toFixed(2)),
          lateCharge: formatDollars(item.lateChrgAlloc.toFixed(2)),
          other: formatDollars(other.toString()),
          // otherDesc: otherStr,
        } as Payment);
      }
    });
    //reverse will display the payments newest to oldest to the user
    payments.reverse();
    return payments;
  }, [servicingLoanPayments, isPaymentApplicableBasedOnMigration, loanNumber, getRemovedDuplicatesServPayments]);

  const {
    data: paymentList,
    isFetching: fetchingPaymentList,
    isLoading: loadingPaymentList,
  } = useQuery({
    queryKey: [loanNumber, token, 'filteredBorrowerServicingPayments', servicingLoanPayments, getEventDateInfo],
    queryFn: ({ signal }: QueryFunctionContext) => getFilteredServicingPayments(),
    refetchOnWindowFocus: false,
  });

  const getPaymentsDisplay = useMemo(() => {
    if(!paymentList || loadingPaymentList) return [];
    const paymentsDisplay = [];

    paymentList.forEach(payment => {
      const payMap = new Map();
      payMap.set('Description', payment?.description);
      // payMap.set('Date', payment?.date);
      payMap.set('Amount', payment?.amount);
      payMap.set('Balance', payment?.balance);
      payMap.set('Paid-To Date', payment?.paidTo);
      payMap.set('Principal', payment?.principal);
      payMap.set('Interest', payment?.interest);
      payMap.set('Additional Principal', payment?.prinCurlAlloc);
      payMap.set('Escrow', payment?.escrow);
      payMap.set('Late Charge', payment?.lateCharge);
      payMap.set('Other', payment?.other);
      // payMap.set('Other Description', payment?.otherDesc);

      paymentsDisplay.push(payMap);
    })
    return paymentsDisplay;
  }, [paymentList]);

  const [numShownElements, setNumShownElements] = useState(0);

  const isMobile = useScreenWidth();
  const isTablet = useScreenWidth('(769px <= width <= 1024px)');
  const isDesktop = useScreenWidth('(min-width: 1025px)');

  useEffect(() => {
    if(isMobile) setNumShownElements(4);
    else if(isTablet) setNumShownElements(5);
    else if(isDesktop) setNumShownElements(8);
    else setNumShownElements(0);
  }, [isMobile, isTablet, isDesktop]);

  useEffect(() => {
    if (!isStrEmptyOrNull(servicingErrorMessages)) {
      enqueueSnackbar(servicingErrorMessages, { variant: 'error' });
    }
  }, [servicingErrorMessages, enqueueSnackbar]);

  return (
    <Fragment>
      {(loadingServicing || loadingPaymentList || fetchingPaymentList) && <LoadingModal open={true} />}

      {(isDesktop === null || !isDesktop) && (
        <EnvelopeWithBox blockName='Pay History'>
          <div>
              {paymentList?.map((row,index) => (
                <ExpandableRow
                  key={index}
                  labelValues={getPaymentsDisplay[index]}
                  primaryElement={formatAbbrevDateMMddyyyy(row.date)}
                  featuredElements={1}
                  shownElements={numShownElements} />
              ))}
          </div>
        </EnvelopeWithBox>
      )}

      {isDesktop && (
        <EnvelopeWithBox blockName='Pay History'>
          <div>
            <div className={Style.height}>
              <Table stickyHeader aria-label='sticky table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' className={Style.headerCell}>
                      Date
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Description
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Amount
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Paid-To Date
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Principal
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Additional Principal
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Balance
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Interest
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Escrow
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Late Charge
                    </TableCell>
                    <TableCell align='center' className={Style.headerCell}>
                      Other
                    </TableCell>
                    {/* <TableCell align='center' className={Style.headerCell}>
                      Other Description
                    </TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentList?.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell component='th' scope='row' align='center' className={Style.cell}>
                        {row.date}
                      </TableCell>
                      <TableCell align='center' className={Style.cell}>
                        {row.description}
                      </TableCell>
                      <NumericCell value={row.amount} />
                      <TableCell align='center' className={Style.cell}>
                        {row.paidTo}
                      </TableCell>
                      <NumericCell value={row.principal} />
                      <NumericCell value={row.prinCurlAlloc} />
                      <NumericCell value={row.balance} />
                      <NumericCell value={row.interest} />
                      <NumericCell value={row.escrow} />
                      <NumericCell value={row.lateCharge} />
                      <NumericCell value={row.other} />
                      {/* <TableCell align='center' className={Style.cell}>
                        {row.otherDesc}
                      </TableCell> */}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </EnvelopeWithBox>
      )}
    </Fragment>
  );
}

const BorrowerPayHistoryPage = () => {
  return (
    <SnackbarProvider maxSnack={3} preventDuplicate anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <LoanHistoryProvider>
        <BorrowerPayHistory />
      </LoanHistoryProvider>
    </SnackbarProvider>
  );
};

export default BorrowerPayHistoryPage;
